import { FunctionComponent, useEffect, useState } from 'react'
import { AppCol, AppRadio, AppRow } from '@/shared/ui'
import { UNITS } from '../../constants/constants'
import { Product } from '../admin-product-card/product-types'
import { NS_MODE, NumberSpinner } from '../number-spinner/number-spinner'

type RejectItemLabels = {
  [key: string]: string
}

/**
 * Label texts.
 */
const LABELS: RejectItemLabels = {
  all: 'All',
  specificQuantity: 'Specific quantity'
}

/**
 * Properties type for RejectItem component.
 */
export type RejectItemProps = {
  className?: string
  product?: Product
  onChange?: (value: number) => void
  copy?: (text: string) => string
}

/**
 * Component that shows UI for reject items in a job.
 *
 * @param param0 {RejectItemProps} Properties
 */
export const RejectItem: FunctionComponent<RejectItemProps> = ({
  className = '',
  product = {
    name: 'Long long long long long long name',
    um: ''
  },
  onChange = () => {},
  copy = (text: string): string => (LABELS[text] ? LABELS[text] : text)
}: RejectItemProps): JSX.Element => {
  const { name, quantity, contentUnits, um } = product

  const [allItems, setAllItems] = useState(false)
  const [currentUnit, setCurrentUnit] = useState(UNITS[um ?? ''])
  const [currentValue, setCurrentValue] = useState<number>(currentUnit ? Math.min(0.1, quantity ?? 0.1) : 1)

  const change = (value: number): void => {
    setCurrentValue(value)
    onChange(value)
  }

  const onChangeAll = (all: boolean) => {
    setAllItems(all)
    onChange(all ? quantity ?? 1 : currentValue)
  }

  useEffect(() => {
    setCurrentUnit(UNITS[um ?? ''])
  }, [product?.um])

  return (
    <div className={`gungnir-reject-item ${className}`}>
      <AppRow className='gungnir-ri-header' wrap={false} justify='space-between'>
        <AppCol sm={{ offset: currentUnit ? 12 : 15, span: 3 }} xs={{ offset: 12 }} className='text-center'>
          {copy('all')}
        </AppCol>
        <AppCol sm={currentUnit ? 9 : 6} xs={9} className='text-center'>
          {copy('specificQuantity')}
        </AppCol>
      </AppRow>
      <div className='gungnir-ri-product-list'>
        <AppRow className='gungnir-ri-product' align='middle' wrap={false} justify='space-between'>
          <AppCol sm={currentUnit ? 12 : 15} xs={12} className='ellipsis'>
            <span className='gungnir-ri-product-name'>{name}</span>
            {contentUnits && <span> · {contentUnits}</span>} <br />
            <span className='gungnir-ri-product-units'>
              {quantity} {UNITS[um ?? '']?.name ?? ''}
            </span>
          </AppCol>
          <AppCol span={3} className='text-center'>
            <AppRadio checked={allItems} onClick={() => onChangeAll(true)}></AppRadio>
          </AppCol>
          <AppCol sm={currentUnit ? 9 : 6} xs={9} className='text-center'>
            <div className='gungnir-ri-quantity'>
              <div>
                <AppRadio checked={!allItems} onClick={() => onChangeAll(false)}></AppRadio>
              </div>
              <NumberSpinner
                value={currentUnit ? Math.min(0.1, quantity ?? 0.1) : 1}
                max={quantity}
                of={quantity}
                mode={NS_MODE.ADD}
                edit={!allItems}
                editable={!!currentUnit}
                showHoverButtons={!allItems}
                showReadyButton={false}
                allowEditOnBlur
                min={currentUnit ? 0.001 : 1}
                decimals={3}
                onChange={change}
                step={currentUnit ? 0.1 : 1}
                um={currentUnit?.name}
              ></NumberSpinner>
            </div>
          </AppCol>
        </AppRow>
      </div>
    </div>
  )
}
